<template>
  <div>
    <q-card flat>
      <q-card-section>
        <div class="header">
          <span class="text-h4">
            {{ course.OLGDP001 }}
          </span>
        </div>
        <div class="text-subtitle2">
          <span v-if="course.OLGDP026 !== PREMIUM_DATA_MASK">{{
            course.OLGDP026
          }}</span>
        </div>
        <div class="text-caption">{{ course.OLGDP021 }}</div>
        <div class="full-width row no-wrap items-start items-center">
          <div v-if="course.OLGDP027 !== PREMIUM_DATA_MASK">
            <span class="text-overline">{{ course.OLGDP027 }}</span>
          </div>
          <div>
            <q-chip
              class="inset-shadow"
              :color="colorComputed"
              :text-color="chipTextColorComputed"
              size="md"
              >{{ course.OLGDP003 }}</q-chip
            >
          </div>
        </div>
        <div class="q-mt-sm q-gutter-sm">
          <q-btn
            :to="{
              name: 'UserCourseRating',
              params: {
                courseId: this.course.id,
              },
            }"
            type="a"
            color="secondary"
            text-color="white"
            size="sm"
            :icon="isPremium ? 'rate_review' : 'lock'"
            title="Write a Review"
            label="Write a Review"
            :disable="!isPremium"
          ></q-btn>
          <q-btn
            v-if="isInWishlist"
            color="amber-9"
            text-color="black"
            size="sm"
            :icon="isPremium ? 'flag' : 'lock'"
            title="Remove from Wishlist"
            label="Remove from Wishlist"
            @click="toggleInWishlist"
            :disable="!isPremium"
          ></q-btn>
          <q-btn
            v-else
            color="amber-5"
            text-color="black"
            size="sm"
            :icon="isPremium ? 'outlined_flag' : 'lock'"
            title="Add to Wishlist"
            label="Add to Wishlist"
            @click="toggleInWishlist"
            :disable="!isPremium"
          ></q-btn>
          <q-btn
            v-if="isInCoursesPlayed"
            color="green-9"
            size="sm"
            :icon="isPremium ? 'done' : 'lock'"
            title="Mark as not Played"
            label="Mark as not Played"
            @click="toggleCoursePlayed"
            :disable="!isPremium"
          ></q-btn>
          <q-btn
            v-else
            color="green-3"
            text-color="black"
            size="sm"
            :icon="isPremium ? 'done_outline' : 'lock'"
            title="Mark as Played"
            label="Mark as Played"
            @click="toggleCoursePlayed"
            :disable="!isPremium"
          ></q-btn>
          <q-btn
            outline
            color="primary"
            size="sm"
            href="https://forms.gle/YrSqGwJuPrgodbV68"
            target="_blank"
            rel="noopener noreferrer"
            type="a"
            label="Suggest an Update"
          ></q-btn>
        </div>
      </q-card-section>
      <q-card-section>
        <q-tabs
          v-model="tab"
          align="left"
          class="text-black"
          active-color="primary"
          indicator-color="primary"
          narrow-indicator
          :breakpoint="0"
        >
          <q-tab name="about" label="About" />
          <q-tab name="reviews" :label="userReviewsTabLabel" />
          <q-tab name="scorecard" label="Scorecard" />
        </q-tabs>
        <q-tab-panels v-model="tab" animated keep-alive>
          <q-tab-panel name="about">
            <div class="course-view">
              <CourseRatingSummary
                :course-id="course.id"
                @update-total-ratings="onUpdateTotalRatings"
              ></CourseRatingSummary>
              <div
                class="q-mt-sm course-view__category"
                v-for="(category, index) of filterCategories"
                :key="index"
              >
                <div :id="category.id" class="text-subtitle1">
                  {{ category.label }}
                </div>
                <div v-if="category.label === 'Location'" class="q-mb-sm">
                  <div class="q-mb-md">
                    <GmapMap
                      :center="centerCoords"
                      :zoom="16"
                      map-type-id="satellite"
                      style="width: 280px; height: 240px"
                      :options="{
                        disableDefaultUI: true,
                        keyboardShortcuts: false,
                        zoomControl: false,
                      }"
                    >
                      <GmapMarker :position="centerCoords"> </GmapMarker>
                    </GmapMap>
                  </div>
                  <div class="q-mb-sm q-gutter-sm">
                    <q-btn
                      color="secondary"
                      text-color="white"
                      type="a"
                      :href="directionsURL"
                      title="Get Driving Directions"
                      label="Get Driving Directions"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      icon="directions"
                      rounded
                    ></q-btn>
                    <q-btn
                      v-if="course.OLGDP010"
                      color="secondary"
                      text-color="white"
                      type="a"
                      :href="course.OLGDP010"
                      title="Open Course Website"
                      label="Open Course Website"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      icon="link"
                      rounded
                    ></q-btn>
                  </div>
                </div>
                <div class="q-pb-sm">
                  <span
                    class="q-pb-sm text-subtitle2 dp-pair"
                    v-for="dp in configGroupedByCategory[category.label]"
                    :key="dp.name"
                  >
                    <span v-if="dp.name === 'OLGDP006'">
                      <span v-if="course['OLGDP006_formatted']">
                        <span class="text-weight-bold">{{ dp.label }}</span
                        >:
                        <span>{{ course["OLGDP006_formatted"] }}</span>
                      </span>
                    </span>

                    <span v-else-if="dp.name === 'OLGDP010'"> </span>

                    <span v-else-if="course[dp.name] === 'Y'">
                      <span class="">{{ dp.label }}</span>
                      <q-icon name="check" class="text-green" size="16px" />
                    </span>

                    <span v-else-if="dp.name === 'OLGDP012' && course[dp.name]">
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course["OLGDP012_formatted"] }}</span>
                    </span>

                    <span v-else-if="dp.name === 'OLGDP098' && course[dp.name]">
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course["OLGDP098_formatted"] }}</span>
                    </span>

                    <span v-else-if="dp.name === 'OLGDP091'">
                      <span class=""
                        ><a
                          :href="course.OLGDP091"
                          target="_blank"
                          rel="noopener noreferrer"
                          >{{ dp.label }}</a
                        ></span
                      >
                    </span>

                    <span v-else-if="dp.name === 'OLGDP092' && course[dp.name]">
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course["OLGDP092_formatted"] }}</span>
                    </span>

                    <span v-else-if="dp.name === 'OLGDP093' && course[dp.name]">
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course["OLGDP093_formatted"] }}</span>
                    </span>

                    <span v-else-if="dp.name === 'OLGDP094' && course[dp.name]">
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course["OLGDP094_formatted"] }}</span>
                    </span>

                    <span v-else>
                      <span class="text-weight-bold">{{ dp.label }}</span
                      >:
                      <span v-if="course[dp.name] === PREMIUM_DATA_MASK">
                        <span title="Please login or register for an account."
                          ><q-icon name="lock"></q-icon
                        ></span> </span
                      ><span v-else>{{ course[dp.name] }}</span>
                    </span>
                  </span>
                </div>
              </div>
              <div
                v-if="isPremium"
                class="q-mt-sm q-pb-sm course-view__category"
              >
                <WeatherSummary :zip="course.OLGDP112"></WeatherSummary>
              </div>
            </div>
            <div v-if="isPremium" class="q-mt-sm">
              <div class="text-subtitle1">My Course Notes</div>
              <div class="q-pb-md">
                <q-input
                  v-model="notes"
                  filled
                  type="textarea"
                  autogrow
                  :loading="loadingNotes"
                  style="width: 310px"
                />
              </div>
              <div class="q-pb-sb">
                <q-btn
                  label="Save"
                  type="submit"
                  color="primary"
                  @click="save"
                />
              </div>
            </div>
          </q-tab-panel>
          <q-tab-panel name="reviews"
            ><div class="q-mt-sm">
              <CourseReviews :course-id="course.id"></CourseReviews>
            </div>
          </q-tab-panel>
          <q-tab-panel name="scorecard"
            ><div>
              <Scorecard :course="course"></Scorecard>
            </div>
          </q-tab-panel>
        </q-tab-panels>
      </q-card-section>
    </q-card>
  </div>
</template>
<script>
import camelCase from "lodash/camelCase";
import groupBy from "lodash/groupBy";
import { mapGetters, mapState } from "vuex";
import { PREMIUM_DATA_MASK } from "@/constants";
import WeatherSummary from "@/components/WeatherSummary.vue";
import CourseRatingSummary from "@/components/CourseRatingSummary.vue";
import CourseReviews from "@/components/CourseReviews.vue";
import Scorecard from "@/components/Scorecard.vue";
import { addCourseNotes, getCourseNotes } from "@/apis/course-notes-service";

export default {
  name: "FullCourseView",
  components: {
    WeatherSummary,
    CourseRatingSummary,
    CourseReviews,
    Scorecard,
  },
  props: {
    course: {
      required: true,
      type: Object,
      default() {
        return {};
      },
    },
    datapoints: {
      required: true,
      type: Array,
      default() {
        return [];
      },
    },
    centerCoords: {
      required: true,
      type: Object,
      default() {
        return [];
      },
    },
    directionsURL: {
      required: true,
      type: String,
      default() {
        return [];
      },
    },
    wishlistCourseIds: {
      required: false,
      type: Array,
      default() {
        return [];
      },
    },
    coursePlayedCourseIds: {
      required: false,
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      loadingNotes: false,
      courseTypeColorMap: {
        muni: "purple-8",
        public: "green-8",
        private: "red-8",
        resort: "blue-8",
        semiPrivate: "yellow-8",
      },
      notes: "",
      newNotes: false,
      tab: "about",
      totalRatings: 0,
    };
  },
  beforeCreate() {
    this.PREMIUM_DATA_MASK = PREMIUM_DATA_MASK;
  },
  mounted() {},
  computed: {
    ...mapState(["idToken"]),
    ...mapGetters(["isPremium"]),
    courseViewColumnsConfig() {
      return this.datapoints
        .filter(
          datapoint =>
            datapoint.id !== "OLGDP001" &&
            datapoint.id !== "OLGDP003" &&
            datapoint.id !== "OLGDP026" &&
            datapoint.id !== "OLGDP027",
        )
        .filter(datapoint => Boolean(this.course[datapoint.id]))
        .map(dataPoint => {
          return {
            category: dataPoint.category,
            name: dataPoint.id,
            label: dataPoint.label,
          };
        });
    },
    configGroupedByCategory() {
      return groupBy(this.courseViewColumnsConfig, "category");
    },
    filterCategories() {
      return Object.keys(this.configGroupedByCategory).map(category => {
        const id = camelCase(category);
        const label = category;

        return {
          id,
          label,
        };
      });
    },
    isInWishlist() {
      return this.wishlistCourseIds.includes(this.course.id);
    },
    isInCoursesPlayed() {
      return this.coursePlayedCourseIds.includes(this.course.id);
    },
    courseTypeId() {
      return camelCase(this.course.OLGDP003);
    },
    colorComputed() {
      return this.courseTypeColorMap[this.courseTypeId];
    },
    chipTextColorComputed() {
      const dark = this.courseTypeId === "semiPrivate";

      return dark ? "" : "white";
    },
    userReviewsTabLabel() {
      const totalRatings = this.totalRatings;
      const label = `User Reviews (${totalRatings})`;

      return label;
    },
  },
  methods: {
    getDatapointById(id) {
      return this.datapoints.find(dp => dp.id === id);
    },
    toggleInWishlist() {
      this.$emit("toggle-wishlist", this.course.id);
    },
    toggleCoursePlayed() {
      this.$emit("toggle-course-played", this.course.id);
    },
    fetchCourseNotes() {
      this.loadingNotes = true;
      return getCourseNotes(this.idToken, this.course.id)
        .then(row => {
          if (!row) {
            return;
          }
          {
            this.notes = row.notes;
          }
        })
        .catch(error => {
          console.error(error);
          this.$q.notify({
            message: `There was an error getting notes: ${error.message}`,
            color: "negative",
            icon: "warning",
          });
        })
        .finally(() => {
          this.loadingNotes = false;
        });
    },
    save() {
      this.loadingNotes = true;
      const token = this.idToken;
      const courseId = this.course.id;
      const notes = { notes: this.notes };

      return addCourseNotes(token, courseId, notes)
        .then(row => {
          this.notes = row.notes;
        })
        .then(() => {
          this.$q.notify({
            position: "bottom",
            type: "positive",
            message: "Your notes have been saved.",
          });

          return this.$store.dispatch("fetchNotesCourseIdsByUid");
        })
        .catch(error => {
          console.error(error);
          this.$q.notify({
            message: `There was an error adding notes: ${error.message}`,
            color: "negative",
            icon: "warning",
          });
        })
        .finally(() => {
          this.loadingNotes = false;
        });
    },
    onUpdateTotalRatings(count) {
      this.totalRatings = count;
    },
  },
  watch: {
    isPremium: {
      handler: function(newVal) {
        if (!newVal) {
          return;
        }

        this.fetchCourseNotes();
      },

      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.header {
  font-family: Georgia, "Times New Roman", Times, serif;
}

.course-view {
  &__category {
    border-bottom: solid 1px #dddddd;
  }
}

.dp-pair {
  margin-right: 14px;
}
</style>
